import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { Audio, AVPlaybackStatus } from "expo-av";
import { useActiveArticle } from "../features/news-feed/hooks/useActiveArticle";
import { useAudioFunctions } from "../hooks/useAudioFunctions";
import { AUDIO_MODE } from "../shared/types/DataTypes";
import { trackInteraction } from "../analytics/analyticsUtil";
import { ACTION_TYPE, ANALYTICS_EVENT } from "../analytics/types";
import { ROUTES } from "../shared/constants/routes";

/* -------------------------------------------------------------------------- */
/* TYPES */
/* -------------------------------------------------------------------------- */

type PlaybackIntent = "play" | "pause" | "toggle";

type AudioState =
  | "idle"
  | "loading"
  | "ready"
  | "playing"
  | "ended"
  | "error";

interface AudioContextType {
  isPlaying: boolean;
  duration: number;
  position: number;
  rate: number;
  isLoading: boolean;
  mode: AUDIO_MODE;

  /** Call before switching active article so load() will auto-play when the new track is ready. */
  preparePlayIntent: () => void;
  togglePlayback: (intent?: PlaybackIntent) => Promise<void>;
  setPlaybackFocus: (focused: boolean) => void;
  stopAndUnload: () => Promise<void>;

  seekForward: () => Promise<void>;
  seekBackward: () => Promise<void>;
  setRate: (rate: number) => Promise<void>;
  toggleRate: () => Promise<void>;
  seekTo: (position: number) => Promise<void>;

  sound: React.MutableRefObject<Audio.Sound | null>;
  setMode: (mode: AUDIO_MODE) => void;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

/* -------------------------------------------------------------------------- */
/* PROVIDER */
/* -------------------------------------------------------------------------- */

export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { activeArticle } = useActiveArticle();
  const { toggleNext } = useAudioFunctions();

  const sound = useRef<Audio.Sound | null>(null);
  const focusCountRef = useRef(0);
  const loadToken = useRef(0);
  const wasPlayingRef = useRef(false);
  const lastLoadedUrl = useRef<string | null>(null);
  const playLock = useRef(false);
  const playIntentRef = useRef(false);
  /** Article id the current (or last) loaded sound belongs to — used to resume position on same-article URL change (e.g. language). */
  const lastLoadedArticleIdRef = useRef<string | null>(null);

  const [audioState, setAudioState] = useState<AudioState>("idle");
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(1);
  const [position, setPosition] = useState(0);
  const [rate, setRateState] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState<AUDIO_MODE>("gist");

  /* -------------------------------------------------------------------------- */
  /* HELPERS */
  /* -------------------------------------------------------------------------- */

  const getAudioUrl = () =>
    mode === "gist"
      ? activeArticle?.audio || activeArticle?.audioFullStory
      : activeArticle?.audioFullStory || activeArticle?.audio;

  /** Before swapping the sound for a new URL, keep time/progress when it is still the same story (language/mode/CDN change). */
  const captureResumeSnapshot =
    useCallback(async (): Promise<{
      positionMs: number;
      shouldPlay: boolean;
    } | null> => {
      try {
        if (
          !sound.current ||
          !activeArticle?.id ||
          activeArticle.id !== lastLoadedArticleIdRef.current
        ) {
          return null;
        }
        const s = await sound.current.getStatusAsync();
        if (!s.isLoaded) return null;
        return {
          positionMs: s.positionMillis,
          shouldPlay: s.isPlaying,
        };
      } catch {
        return null;
      }
    }, [activeArticle?.id]);

  /* -------------------------------------------------------------------------- */
  /* PLAYBACK STATUS */
  /* -------------------------------------------------------------------------- */

  const onPlaybackStatusUpdate = useCallback(
    (status: AVPlaybackStatus) => {
      if (!status.isLoaded) return;
      setPosition(status.positionMillis);
      setDuration(status.durationMillis ?? 1);
      setIsPlaying(status.isPlaying);

      if (status.isPlaying && !wasPlayingRef.current) {
        trackInteraction({
          event: ANALYTICS_EVENT.INTERACTION,
          action: ACTION_TYPE.CLICK,
          screen_name: ROUTES.RADIO_PLAYER,
          component: "audio_play_start",
          context: {
            cta: "AUDIO_PLAY_START",
            article_id: activeArticle?.id,
            audio_mode: mode,
          },
        });
      }
      wasPlayingRef.current = status.isPlaying;

      if (status.didJustFinish) {
        trackInteraction({
          event: ANALYTICS_EVENT.INTERACTION,
          action: ACTION_TYPE.CLICK,
          screen_name: ROUTES.RADIO_PLAYER,
          component: "audio_completed",
          context: {
            cta: "AUDIO_COMPLETED",
            article_id: activeArticle?.id,
            audio_mode: mode,
            duration_ms: status.durationMillis ?? duration,
          },
        });
        setAudioState("ended");

        if (playIntentRef.current && focusCountRef.current > 0) {
          toggleNext?.();
        }
        return;
      }

      setAudioState(status.isPlaying ? "playing" : "ready");
    },
    [activeArticle?.id, duration, mode, toggleNext]
  );

  /* -------------------------------------------------------------------------- */
  /* SAFE UNLOAD */
  /* -------------------------------------------------------------------------- */

  const unload = async () => {
    const current = sound.current;
    if (!current) return;

    // Prevent race conditions
    sound.current = null;
    lastLoadedUrl.current = null;

    try {
      await current.stopAsync().catch(() => {});
      await current.unloadAsync().catch(() => {});
    } catch {}

    setAudioState("idle");
    setIsPlaying(false);
    setPosition(0);
    setDuration(1);
    wasPlayingRef.current = false;
    lastLoadedArticleIdRef.current = null;
  };

  /* -------------------------------------------------------------------------- */
  /* LOAD */
  /* -------------------------------------------------------------------------- */

  const load = async (
    url: string,
    resume: { positionMs: number; shouldPlay: boolean } | null = null
  ) => {
    if (!url) return;

    await unload();

    const token = ++loadToken.current;
    lastLoadedUrl.current = url;

    setIsLoading(true);
    setAudioState("loading");

    try {
      const { sound: newSound } = await Audio.Sound.createAsync(
        { uri: url },
        { shouldPlay: false },
        onPlaybackStatusUpdate
      );

      if (token !== loadToken.current) {
        await newSound.unloadAsync().catch(() => {});
        return;
      }

      sound.current = newSound;
      if (activeArticle?.id) {
        lastLoadedArticleIdRef.current = activeArticle.id;
      }
      try {
        await newSound.setRateAsync(rate, true);
      } catch {}
      setAudioState("ready");

      if (resume) {
        const status = await newSound.getStatusAsync();
        const dur = status.isLoaded ? (status.durationMillis ?? 0) : 0;
        const safeDur = Math.max(0, dur - 80);
        const seekMs = Math.min(Math.max(0, resume.positionMs), safeDur);
        await newSound.setPositionAsync(seekMs).catch(() => {});
        playIntentRef.current = resume.shouldPlay;
        if (resume.shouldPlay && focusCountRef.current > 0) {
          await newSound.playAsync().catch(() => {});
        }
      } else if (playIntentRef.current && focusCountRef.current > 0) {
        await newSound.playAsync().catch(() => {});
      }
    } catch {
      setAudioState("error");
    } finally {
      token === loadToken.current && setIsLoading(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /* AUTO LOAD */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    const url = getAudioUrl();
    if (!url) return;
    if (url === lastLoadedUrl.current) {
      // Playlist can arm playIntent then switch article id while the CDN URL is unchanged — still resume.
      if (playIntentRef.current && focusCountRef.current > 0 && sound.current) {
        sound.current.playAsync().catch(() => {});
      }
      return;
    }

    void (async () => {
      const resume = await captureResumeSnapshot();
      await load(url, resume);
    })();
  }, [
    activeArticle?.id,
    mode,
    activeArticle?.audio,
    activeArticle?.audioFullStory,
    captureResumeSnapshot,
  ]);

  /* -------------------------------------------------------------------------- */
  /* PROVIDER UNMOUNT CLEANUP (CRITICAL FIX) */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    return () => {
      const current = sound.current;
      sound.current = null;
      current?.unloadAsync().catch(() => {});
    };
  }, []);

  /* -------------------------------------------------------------------------- */
  /* PUBLIC API */
  /* -------------------------------------------------------------------------- */

  const setPlaybackFocus = (focused: boolean) => {
    focusCountRef.current += focused ? 1 : -1;
    focusCountRef.current = Math.max(0, focusCountRef.current);
    
    if (focusCountRef.current === 0) {
      playIntentRef.current = false;
      sound.current?.pauseAsync().catch(() => {});
    }
  };

  const stopAndUnload = useCallback(async () => {
    playIntentRef.current = false;
    await unload();
  }, []);

  const preparePlayIntent = useCallback(() => {
    playIntentRef.current = true;
  }, []);

  const togglePlayback = async (intent: PlaybackIntent = "toggle") => {
    if (focusCountRef.current === 0) return;
    if (playLock.current) return;

    playLock.current = true;

    try {
      const url = getAudioUrl();
      if (!url) return;

      playIntentRef.current =
        intent === "play"
          ? true
          : intent === "pause"
          ? false
          : !isPlaying;

      if (!sound.current || lastLoadedUrl.current !== url) {
        const resume = await captureResumeSnapshot();
        await load(url, resume);
        return;
      }

      const status = await sound.current.getStatusAsync();
      if (!status.isLoaded) return;

      if (!playIntentRef.current) {
        await sound.current.pauseAsync();
        return;
      }

      if (status.positionMillis >= (status.durationMillis ?? 0)) {
        await sound.current.setPositionAsync(0);
      }

      await sound.current.playAsync();
    } catch (e: any) {
      if (e?.name !== "AbortError") console.warn(e);
    } finally {
      playLock.current = false;
    }
  };

  const seekForward = async () => {
    if (!sound.current) return;
    const s = await sound.current.getStatusAsync();
    if (!s.isLoaded) return;
    await sound.current.setPositionAsync(
      Math.min(s.positionMillis + 15000, s.durationMillis ?? 0)
    );
  };

  const seekBackward = async () => {
    if (!sound.current) return;
    const s = await sound.current.getStatusAsync();
    if (!s.isLoaded) return;
    await sound.current.setPositionAsync(
      Math.max(s.positionMillis - 15000, 0)
    );
  };

  const setRate = async (newRate: number) => {
    setRateState(newRate);
    if (!sound.current) return;
    await sound.current.setRateAsync(newRate, true);
  };

  const toggleRate = async () => {
    const next = rate === 1 ? 1.2 : rate === 1.2 ? 1.5 : rate === 1.5 ? 2 : 1;
    await setRate(next);
  };

  const seekTo = async (pos: number) => {
    if (!sound.current) return;
    await sound.current.setPositionAsync(pos);
  };

  /* -------------------------------------------------------------------------- */

  return (
    <AudioContext.Provider
      value={{
        isPlaying,
        duration,
        position,
        rate,
        isLoading,
        preparePlayIntent,
        togglePlayback,
        setPlaybackFocus,
        stopAndUnload,
        seekForward,
        seekBackward,
        setRate,
        toggleRate,
        seekTo,
        sound,
        mode,
        setMode,
      }}
    >
      {children}
    </AudioContext.Provider>
  );
};

/* -------------------------------------------------------------------------- */
/* HOOK */
/* -------------------------------------------------------------------------- */

export const useAudioContext = () => {
  const ctx = useContext(AudioContext);
  if (!ctx)
    throw new Error("useAudioContext must be used within AudioProvider");
  return ctx;
};
